/**
 * 倉儲模組路由 — receipts 與 material-mgmt settings。
 *
 * 註冊方式（勿改 router/index.js 巨檔）：
 *   export const warehouseRoutes = [ ... ];
 *   並由 adminRoutes.jsx import 合併至 children。
 */
import { Navigate } from 'react-router';

import {
  DOCUMENT_MGMT_READ_CODES,
  INVENTORY_STOCK_READ_CODES,
  PERM,
} from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import CreateEditStockReceiptPage from '#/modules/imsMgmt/receipts/stockReceipt/pages/CreateEditStockReceiptPage';
import StockReceiptDetailPage from '#/modules/imsMgmt/receipts/stockReceipt/pages/StockReceiptDetailPage';
import StockReceiptListPage from '#/modules/imsMgmt/receipts/stockReceipt/pages/StockReceiptListPage';
import StockReceiptRemarkPage from '#/modules/imsMgmt/receipts/stockReceipt/pages/StockReceiptRemarkPage';
import CreateEditStockTransferPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/CreateEditStockTransferPage';
import StockTransferDetailPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/StockTransferDetailPage';
import StockTransferListPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/StockTransferListPage';
import StockTransferRemarkPage from '#/modules/imsMgmt/receipts/stockTransfer/pages/StockTransferRemarkPage';
import CreateEditStockIssuePage from '#/modules/imsMgmt/receipts/stockIssue/pages/CreateEditStockIssuePage';
import StockIssueDetailPage from '#/modules/imsMgmt/receipts/stockIssue/pages/StockIssueDetailPage';
import StockIssueListPage from '#/modules/imsMgmt/receipts/stockIssue/pages/StockIssueListPage';
import StockIssueRemarkPage from '#/modules/imsMgmt/receipts/stockIssue/pages/StockIssueRemarkPage';
import CreateEditStockAdjustmentPage from '#/modules/imsMgmt/receipts/stockAdjustment/pages/CreateEditStockAdjustmentPage';
import StockAdjustmentDetailPage from '#/modules/imsMgmt/receipts/stockAdjustment/pages/StockAdjustmentDetailPage';
import StockAdjustmentListPage from '#/modules/imsMgmt/receipts/stockAdjustment/pages/StockAdjustmentListPage';
import StockAdjustmentRemarkPage from '#/modules/imsMgmt/receipts/stockAdjustment/pages/StockAdjustmentRemarkPage';
import CreateEditStocktakePage from '#/modules/imsMgmt/receipts/stocktake/pages/CreateEditStocktakePage';
import StocktakeDetailPage from '#/modules/imsMgmt/receipts/stocktake/pages/StocktakeDetailPage';
import StocktakeShellPage from '#/modules/imsMgmt/receipts/stocktake/pages/StocktakeShellPage';
import CreateEditStockIssueCategoryPage from '#/modules/imsMgmt/settings/stockIssueCategory/pages/CreateEditStockIssueCategoryPage';
import StockIssueCategoryListPage from '#/modules/imsMgmt/settings/stockIssueCategory/pages/StockIssueCategoryListPage';
import InventorySafetyCheckPage from '#/modules/imsMgmt/inventoryStock/pages/InventorySafetyCheckPage';
import InventoryStockListPage from '#/modules/imsMgmt/inventoryStock/pages/InventoryStockListPage';
import DocumentMgmtHubPage from '#/modules/infra/pages/DocumentMgmtHubPage';
import WarehouseHubPage from '#/modules/infra/pages/WarehouseHubPage';
import { materialMgmtRoute } from '#/modules/infra/router/routes/admin/imsSettings.routes';
import { storePlaceMgmtRoute } from '#/modules/infra/router/routes/admin/storePlace.routes';

export const warehouseRoutes = [
  {
    path: 'warehouse',
    handle: { title: '倉儲' },
    children: [
      {
        index: true,
        element: <WarehouseHubPage />,
      },
      {
        path: 'inventory',
        handle: { title: '庫存' },
        element: (
          <RequirePermission codes={INVENTORY_STOCK_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <InventoryStockListPage />,
          },
        ],
      },
      {
        path: 'safety-stock',
        handle: { title: '安全庫存' },
        element: (
          <RequirePermission codes={INVENTORY_STOCK_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <InventorySafetyCheckPage />,
          },
        ],
      },
      materialMgmtRoute,
      {
        path: 'document-mgmt',
        handle: { title: '單據管理' },
        element: (
          <RequirePermission codes={DOCUMENT_MGMT_READ_CODES} mode="any">
            <DocumentMgmtHubPage />
          </RequirePermission>
        ),
      },
      storePlaceMgmtRoute,
      {
        path: 'receipts/stock-receipt',
        handle: {
          title: '入庫管理',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        element: <RequirePermission code={PERM.STOCK_RECEIPT_READ} />,
        children: [
          {
            index: true,
            element: <StockReceiptListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增入庫單' },
            element: (
              <RequirePermission code={PERM.STOCK_RECEIPT_CREATE}>
                <CreateEditStockReceiptPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯入庫單' },
            element: (
              <RequirePermission code={PERM.STOCK_RECEIPT_UPDATE}>
                <CreateEditStockReceiptPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯入庫備註' },
            element: (
              <RequirePermission code={PERM.STOCK_RECEIPT_UPDATE}>
                <StockReceiptRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '入庫單詳情' },
            element: <StockReceiptDetailPage />,
          },
        ],
      },
      {
        path: 'receipts/stock-transfer',
        handle: {
          title: '轉倉管理',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        element: <RequirePermission code={PERM.STOCK_TRANSFER_READ} />,
        children: [
          {
            index: true,
            element: <StockTransferListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增轉倉單' },
            element: (
              <RequirePermission code={PERM.STOCK_TRANSFER_CREATE}>
                <CreateEditStockTransferPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯轉倉單' },
            element: (
              <RequirePermission code={PERM.STOCK_TRANSFER_UPDATE}>
                <CreateEditStockTransferPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯轉倉備註' },
            element: (
              <RequirePermission code={PERM.STOCK_TRANSFER_UPDATE}>
                <StockTransferRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '轉倉單詳情' },
            element: <StockTransferDetailPage />,
          },
        ],
      },
      {
        path: 'receipts/stock-issue',
        handle: {
          title: '出庫管理',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        element: <RequirePermission code={PERM.STOCK_ISSUE_READ} />,
        children: [
          {
            index: true,
            element: <StockIssueListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增出庫單' },
            element: (
              <RequirePermission code={PERM.STOCK_ISSUE_CREATE}>
                <CreateEditStockIssuePage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯出庫單' },
            element: (
              <RequirePermission code={PERM.STOCK_ISSUE_UPDATE}>
                <CreateEditStockIssuePage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯出庫備註' },
            element: (
              <RequirePermission code={PERM.STOCK_ISSUE_UPDATE}>
                <StockIssueRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '出庫單詳情' },
            element: <StockIssueDetailPage />,
          },
        ],
      },
      {
        path: 'receipts/stock-adjustment',
        handle: {
          title: '調整管理',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        element: <RequirePermission code={PERM.STOCK_ADJUSTMENT_READ} />,
        children: [
          {
            index: true,
            element: <StockAdjustmentListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增調整單' },
            element: (
              <RequirePermission code={PERM.STOCK_ADJUSTMENT_CREATE}>
                <CreateEditStockAdjustmentPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯調整單' },
            element: (
              <RequirePermission code={PERM.STOCK_ADJUSTMENT_UPDATE}>
                <CreateEditStockAdjustmentPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯調整備註' },
            element: (
              <RequirePermission code={PERM.STOCK_ADJUSTMENT_UPDATE}>
                <StockAdjustmentRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '調整單詳情' },
            element: <StockAdjustmentDetailPage />,
          },
        ],
      },
      {
        path: 'receipts/stocktake',
        handle: {
          title: '盤點管理',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        element: <RequirePermission code={PERM.STOCKTAKE_READ} />,
        children: [
          {
            index: true,
            element: <StocktakeShellPage />,
          },
          {
            path: 'create',
            handle: { title: '新增盤點單' },
            element: (
              <RequirePermission code={PERM.STOCKTAKE_CREATE}>
                <CreateEditStocktakePage />
              </RequirePermission>
            ),
          },
          {
            path: 'comparison',
            element: <Navigate to={ROUTES.stocktakeComparison} replace />,
          },
          {
            path: ':id/edit',
            handle: { title: '編輯盤點單' },
            element: (
              <RequirePermission code={PERM.STOCKTAKE_UPDATE}>
                <CreateEditStocktakePage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '盤點單詳情' },
            element: <StocktakeDetailPage />,
          },
        ],
      },
      {
        path: 'receipts/stock-issue-category',
        element: <RequirePermission code={PERM.STOCK_ISSUE_CATEGORY_READ} />,
        handle: {
          title: '出庫類別',
          breadcrumbParents: [
            { title: '單據管理', to: ROUTES.documentMgmt },
          ],
        },
        children: [
          {
            index: true,
            element: <StockIssueCategoryListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增出庫類別' },
            element: (
              <RequirePermission code={PERM.STOCK_ISSUE_CATEGORY_CREATE}>
                <CreateEditStockIssueCategoryPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯出庫類別' },
            element: (
              <RequirePermission code={PERM.STOCK_ISSUE_CATEGORY_UPDATE}>
                <CreateEditStockIssueCategoryPage />
              </RequirePermission>
            ),
          },
        ],
      },
    ],
  },
];
